import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    Box,
    Button,
    IconButton,
    Typography,
    CircularProgress,
    keyframes,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CropFreeIcon from '@mui/icons-material/CropFree'; // "Viewfinder" icon
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

// Define a subtle breathing animation for the viewfinder
const breathe = keyframes`
  0% { transform: scale(1); opacity: 0.3; }
  50% { transform: scale(1.02); opacity: 0.4; }
  100% { transform: scale(1); opacity: 0.3; }
`;

/**
 * Camera-first modal for mobile deficiency pin creation.
 * Provides quick access to camera for field workers on iPad/mobile.
 * Entire screen is tappable to open camera for maximum speed.
 *
 * DOM Structure uses layered approach to prevent mobile touch event issues:
 * - Layer 1 (back): Full-screen tap area for camera
 * - Layer 2 (front): Content overlay (pointerEvents: none, except interactive elements)
 */
function CameraCaptureModal({ open, onClose, onCapture }) {
    const [isProcessing, setIsProcessing] = useState(false);

    // Handle file selection (shared logic for camera and gallery)
    const handleFileSelected = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            // User cancelled the picker - stay on this modal
            return;
        }

        setIsProcessing(true);

        try {
            let processedFile = file;

            // HEIC/HEIF conversion (reuse pattern from AddDeficiencyModal)
            const lowerName = (file.name || '').toLowerCase();
            const lowerType = (file.type || '').toLowerCase();
            const isHeicOrHeif =
                lowerName.endsWith('.heic') ||
                lowerName.endsWith('.heif') ||
                lowerType.includes('heic') ||
                lowerType.includes('heif');

            if (isHeicOrHeif) {
                try {
                    const { default: heic2any } = await import('heic2any');
                    const jpegBlob = await heic2any({
                        blob: file,
                        toType: 'image/jpeg',
                        quality: 0.9,
                    });
                    processedFile = new File(
                        [jpegBlob],
                        lowerName.replace(/\.(heic|heif)$/i, '.jpg') || 'image.jpg',
                        { type: 'image/jpeg' }
                    );
                } catch (conversionError) {
                    console.warn('HEIC/HEIF conversion failed:', conversionError);
                    // Continue with original file - let the upload handle it
                }
            }

            // Create preview URL
            const preview = URL.createObjectURL(processedFile);

            // Pass to parent
            onCapture({
                preview,
                isAnnotated: false,
                file: processedFile,
            });
        } catch (error) {
            console.error('Error processing image:', error);
            // Stay on modal so user can try again
        } finally {
            setIsProcessing(false);
            // Clear input for reuse
            event.target.value = '';
        }
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="camera-capture-modal">
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                }}
            >
                {/* Layer 1: Full-screen tap area for camera (background) */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        cursor: isProcessing ? 'default' : 'pointer',
                        zIndex: 0,
                        transition: 'background-color 0.1s',
                        '&:active': {
                            backgroundColor: 'rgba(0,0,0,0.02)', // Even subtler feedback
                        },
                        '&:focus-within': {
                            boxShadow: 'inset 0 0 0 3px #1652F0',
                        },
                    }}
                >
                    <input
                        type="file"
                        aria-label="Take a photo"
                        accept="image/*,.heic,.HEIC,.heif,.HEIF"
                        capture="environment"
                        onChange={handleFileSelected}
                        disabled={isProcessing}
                        // The native input receives the tap directly so Android does not have to
                        // honor a scripted click on a display:none file control.
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: isProcessing ? 'default' : 'pointer',
                        }}
                    />
                </Box>

                {/* Layer 2: Content overlay (foreground) */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between', // Push content to edges
                        zIndex: 1,
                        pointerEvents: 'none', // Let clicks pass through to tap area
                        p: 3, // Safe area padding
                    }}
                >
                    {/* Top Section: Close Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end', // Right align
                        }}
                    >
                        <IconButton
                            onClick={onClose}
                            sx={{
                                bgcolor: 'grey.50',
                                color: 'grey.800',
                                width: 48,
                                height: 48,
                                '&:hover': { bgcolor: 'grey.200' },
                                pointerEvents: 'auto', // Enable clicks
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                transition: 'transform 0.2s',
                                '&:active': { transform: 'scale(0.95)' },
                            }}
                            aria-label="Close camera mode"
                        >
                            <CloseIcon sx={{ fontSize: 26 }} />
                        </IconButton>
                    </Box>

                    {/* Center Section: Viewfinder & Primary CTA */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                            opacity: isProcessing ? 0.5 : 1,
                            transition: 'opacity 0.2s',
                            mt: -6, // Optical centering correction (offsetting bottom button)
                        }}
                    >
                        {isProcessing ? (
                            <>
                                <CircularProgress
                                    size={50}
                                    thickness={3}
                                    sx={{ color: 'text.secondary' }}
                                />
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ mt: 2, fontWeight: 500 }}
                                >
                                    Processing...
                                </Typography>
                            </>
                        ) : (
                            <>
                                {/* Viewfinder Visual */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        color: 'text.primary',
                                        mb: 1,
                                        animation: `${breathe} 4s ease-in-out infinite`,
                                    }}
                                >
                                    <CropFreeIcon sx={{ fontSize: 140 }} />
                                </Box>

                                {/* Primary Instruction */}
                                <Typography
                                    variant="h6"
                                    textAlign="center"
                                    color="text.primary"
                                    fontWeight={600}
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.15em',
                                        opacity: 0.8,
                                        fontSize: '0.9rem', // More refined size
                                    }}
                                >
                                    Tap Screen to Capture
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Bottom Section: Gallery Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            pb: 2, // Bottom safe area
                        }}
                    >
                        <Button
                            component="label"
                            variant="outlined"
                            size="large"
                            fullWidth
                            startIcon={<PhotoLibraryIcon />}
                            disabled={isProcessing}
                            sx={{
                                height: 56, // Large touch target
                                borderRadius: 50, // Pill shape
                                bgcolor: 'white',
                                color: 'text.primary',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                pointerEvents: 'auto', // Enable clicks
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: '0 8px 30px rgba(0,0,0,0.08)', // Soft diffused shadow
                                border: '1px solid',
                                borderColor: 'grey.100',
                                maxWidth: 320, // Compact max width
                                transition: 'all 0.2s',
                                '&:hover': {
                                    bgcolor: 'grey.50',
                                    borderColor: 'grey.300',
                                    transform: 'translateY(-2px)',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                },
                                '&:focus-within': {
                                    outline: '2px solid #1652F0',
                                    outlineOffset: '2px',
                                },
                            }}
                        >
                            Choose from Photos
                            <input
                                type="file"
                                aria-label="Choose a photo from the library"
                                accept="image/*,.heic,.HEIC,.heif,.HEIF"
                                onChange={handleFileSelected}
                                disabled={isProcessing}
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                    cursor: isProcessing ? 'default' : 'pointer',
                                }}
                            />
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}

CameraCaptureModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCapture: PropTypes.func.isRequired,
};

export default CameraCaptureModal;
